import type { Dispatch, FC, ReactNode } from 'react';
import { createContext, useContext, useReducer } from 'react';

import { useBankDetails } from 'common/hooks/useBankDetails';
import Loader from 'components/Loader';

import { Action, newExpenseClaimRequestReducer, State } from './reducer';

type Context = State & {
  shouldSkipRecipientInformation: boolean;
  dispatch: Dispatch<Action>;
};

interface NewExpenseClaimRequestProviderProps {
  children: ReactNode;
}

const NewExpenseClaimRequestContext = createContext<Context | undefined>(
  undefined,
);

export const useNewExpenseClaimRequestState = (): Context => {
  const context = useContext(NewExpenseClaimRequestContext);

  if (!context) {
    throw new Error(
      'useNewExpenseClaimRequestsState must be used within a <NewExpenseClaimRequestsProvider/>',
    );
  }

  return context;
};

export const NewExpenseClaimRequestProvider: FC<
  NewExpenseClaimRequestProviderProps
> = ({ children }) => {
  const [state, dispatch] = useReducer(newExpenseClaimRequestReducer, {
    receiptInformation: {
      amount: {
        total: undefined,
        vatItems: [
          {
            amount: undefined,
            rate: undefined,
          },
        ],
      },
      description: '',
      supportingDocument: undefined,
    },
    recipientInformation: {
      country: 'FR',
      iban: '',
      name: '',
      swiftBic: '',
    },
  });
  const { data, error, loading } = useBankDetails({
    fetchPolicy: 'cache-first',
    onCompleted: (values) => {
      if (values.viewer.profile.bankDetails) {
        dispatch({
          payload: {
            country: values.viewer.profile.bankDetails.iban.slice(0, 2),
            iban: values.viewer.profile.bankDetails.iban,
            name: values.viewer.profile.bankDetails.recipientName,
            swiftBic: values.viewer.profile.bankDetails.swiftBic,
          },
          type: 'SUBMIT_RECIPIENT_INFORMATION',
        });
      }
    },
  });

  const context = {
    ...state,
    dispatch,
    shouldSkipRecipientInformation:
      (error === undefined &&
        data?.viewer.profile.bankDetails?.bankDetailsId !== undefined) ??
      false,
  } satisfies Context;

  return (
    <NewExpenseClaimRequestContext.Provider value={context}>
      {loading ? <Loader /> : children}
    </NewExpenseClaimRequestContext.Provider>
  );
};
